import { QuoteUserDetailsDTO } from "./../../Models/Domain/UserAccess/QuoteUserDetailsDTO";
import { runInAction } from "mobx";
// Libraries
import { action, computed, observable } from "mobx";

// Core
import { ViewModelBase } from "Core/ViewModels";
import { FieldType } from "Core/Utils/Utils";

// Custom
import { Server } from "./../../Globals/AppUrls";
import { ProductSelectorModel } from "Custom/Models/Domain/ProductSelector/ProductSelectorModel";

import { ProductsViewModel } from "./SubViewModels/ProductsViewModel";
import { GuaranteesViewModel } from "./SubViewModels/GuaranteeViewModel";
import { GutterSystemViewModel } from "./SubViewModels/GutterSystemViewModel";
import { QuoteViewModel } from "./SubViewModels/QuoteViewModel";
import { RoofSystemViewModel } from "./SubViewModels/RoofSystemViewModel";
import { StoresInstance } from "Custom/Stores/Stores";
import { AccountStore, ErrorStore } from "Custom/Stores/Domain";
import { QuoteEmailRequest } from "Custom/Models/Domain/ProductSelector/QuoteEmailRequest";

export class ProductSelectorViewModel extends ViewModelBase<ProductSelectorModel> {
    private guaranteesViewModel: GuaranteesViewModel;
    private gutterSystemViewModel: GutterSystemViewModel;
    private productViewModel: ProductsViewModel;
    private quoteViewModel: QuoteViewModel;
    private roofSystemViewModel: RoofSystemViewModel;
    private accountStore: AccountStore = StoresInstance.domain.AccountStore;
    public errorStore: ErrorStore = StoresInstance.domain.ErrorStore;

    @observable canShowSendEmail: boolean = true;

    constructor() {
        super(new ProductSelectorModel());
        this.setDecorators(ProductSelectorModel);

        this.productViewModel = new ProductsViewModel(this.model.Products);

        this.roofSystemViewModel = new RoofSystemViewModel(this.model.RoofSystem);
        this.gutterSystemViewModel = new GutterSystemViewModel(this.model.GutterSystem);

        this.guaranteesViewModel = new GuaranteesViewModel(this.model);
        this.quoteViewModel = new QuoteViewModel(this.model.Quote, this.gutterSystemViewModel);

        this.getProductData();
    }

    // #region Subviewmodel Properties

    public get GuaranteesViewModel(): GuaranteesViewModel {
        return this.guaranteesViewModel;
    }

    public get GutterSystemViewModel(): GutterSystemViewModel {
        return this.gutterSystemViewModel;
    }

    public get ProductsViewModel(): ProductsViewModel {
        return this.productViewModel;
    }

    public get QuoteViewModel(): QuoteViewModel {
        return this.quoteViewModel;
    }

    public get RoofSystemViewModel(): RoofSystemViewModel {
        return this.roofSystemViewModel;
    }

    // #endregion Subviewmodel Properties

    // #region Other Stuff

    @computed
    public get canDisplayResetSection(): boolean {
        return StoresInstance.domain.showResetButton;
    }

    @computed
    public get canDisplayQuoteAndEmail(): boolean {
        return this.model.Quote.gutterProducts.length > 0 || this.model.Quote.roofProducts.length > 0;
    }

    @computed
    public get canDisplayPopup(): boolean {
        return this.errorStore.hasError;
    }

    @action
    private getProductData = async (): Promise<void> => {
        try {
            const apiResult = await this.Get<any>(Server.Api.Quote.GetProductData);

            if (apiResult.wasSuccessful) {
                this.model.GutterSystem.DelcoteProducts.DelcoteGSModel.fromProductDataDto(apiResult.payload.delcoteGSColours);
                this.model.RoofSystem.DelcoteProducts.Delcote10Model.fromProductDataDto(apiResult.payload.delcote10TopcoatColours);
                this.model.RoofSystem.DelcoteProducts.Delcote25Model.fromProductDataDto(apiResult.payload.delcote25BasecoatColours, apiResult.payload.delcote25TopcoatColours);
                this.model.RoofSystem.DelcoteProducts.DelcoteAsbestosModel.fromProductDataDto(
                    apiResult.payload.delcoteAsbestosBasecoatColours,
                    apiResult.payload.delcoteAsbestosTopcoatColours,
                );
                this.model.RoofSystem.SeamsilProducts.Seamsil100Model.fromProductDataDto(apiResult.payload.seamsil100TopcoatColours);
            } else {
                // Error
                this.errorStore.SetErrorMessage("Get Product Data", "There seem to be an issue getting the product data", "Please try again later or talk to support");
            }
        } catch (exception) {
            // Catch
            this.errorStore.SetErrorMessage("Get Product Data", "There seems to be an issue talking to the server.", "Please try again later or talk to support");
        } finally {
            // Finally
        }
    };

    @action
    public sendEmail = async (): Promise<void> => {
        try {
            this.setShowSendEmail(false);

            let userDetails: QuoteUserDetailsDTO | undefined = this.accountStore.getUserDetailsForEmail();

            if (userDetails !== undefined) {
                const request: QuoteEmailRequest = {
                    userDetails: userDetails,
                    request: this.model.toDto(),
                    response: this.quoteViewModel.model.toDto(),
                };

                const apiResult = await this.Post<any>(Server.Api.Quote.SendEmail, request);

                if (apiResult.wasSuccessful) {
                    this.errorStore.SetErrorMessage("Quotation Request", "Successfully sent quotation request.", "Please close this window to do another quote.");
                    this.setShowSendEmail(true);
                    this.clearQuote();
                } else {
                    // Error
                    this.errorStore.SetErrorMessage("Quotation Request", "There seems to be an issue sending the quotation request", "Please try again later or talk to support");
                    this.setShowSendEmail(true);
                }
            }
        } catch (exception) {
            // Catch
            this.errorStore.SetErrorMessage("Quotation Request", "There seems to be an issue sending the quotation request", "Please try again later or talk to support");
            this.setShowSendEmail(true);
        } finally {
            // Finally
        }
    };

    @action
    private setShowSendEmail = (value: boolean) => {
        this.canShowSendEmail = value;
    };

    @action
    private clearQuote = () => {
        this.model.Quote.gutterProducts.clear();
        this.model.Quote.roofProducts.clear();
        this.accountStore.setSubmitted(false);
    };

    // #endregion Other Stuff

    // #region Viewmodel Boilerplate

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    public isFieldValid(fieldName: keyof FieldType<ProductSelectorModel>): boolean {
        return true;
    }

    // #endregion Viewmodel Boilerplate
}
